/** Phase 4 consumes the actual Hono endpoints; a missing route contract fails
 * TypeScript rather than 404ing at runtime. */
import { queryOptions } from "@tanstack/react-query";
import type { InferResponseType } from "hono/client";
import { readJson, rpc, type JsonBody } from "./api";
import { invalidate } from "./queries";
import { queryClient } from "./session";

export type PaymentRequestView = InferResponseType<typeof rpc.api.bookings[":id"]["payment-requests"]["$get"]>["items"][number];

export const paymentRequestsQuery = (bookingId: string) => queryOptions({
  queryKey: ["booking", bookingId, "payment-requests"],
  queryFn: () => readJson(rpc.api.bookings[":id"]["payment-requests"].$get({ param: { id: bookingId } })).then(result => result.items),
  refetchOnWindowFocus: true,
});

async function refreshed<T>(bookingId: string, response: Promise<T>) {
  const result = await response;
  await Promise.all([invalidate.booking(bookingId), queryClient.invalidateQueries({ queryKey: ["booking", bookingId, "payment-requests"] })]);
  return result;
}

export const paymentApi = {
  request: (bookingId: string, json: JsonBody<typeof rpc.api.bookings[":id"]["payment-requests"]["$post"]>) =>
    refreshed(bookingId, readJson(rpc.api.bookings[":id"]["payment-requests"].$post({ param: { id: bookingId }, json }))),
  cancelRequest: (bookingId: string, id: string) =>
    refreshed(bookingId, readJson(rpc.api["payment-requests"][":id"].cancel.$post({ param: { id } }))),
  refund: (bookingId: string, id: string, json: JsonBody<typeof rpc.api.payments[":id"]["refund"]["$post"]>) =>
    refreshed(bookingId, readJson(rpc.api.payments[":id"].refund.$post({ param: { id }, json }))),
  captureHold: (bookingId: string, json: JsonBody<typeof rpc.api.bookings[":id"]["card-hold"]["capture"]["$post"]>) =>
    refreshed(bookingId, readJson(rpc.api.bookings[":id"]["card-hold"].capture.$post({ param: { id: bookingId }, json }))),
  releaseHold: (bookingId: string, json: JsonBody<typeof rpc.api.bookings[":id"]["card-hold"]["release"]["$post"]>) =>
    refreshed(bookingId, readJson(rpc.api.bookings[":id"]["card-hold"].release.$post({ param: { id: bookingId }, json }))),
};
